/**
 * The support queue — Task 13.2, requirements 9.3, 9.6–9.7, and 15.1–15.5.
 *
 * ## What a row may show, and what it may not
 *
 * Public id, customer name, category, status, assignee, and two timestamps. Not the email and not the message
 * body: both are on the detail screen, behind a deliberate navigation, because a queue is a screen somebody
 * leaves open on a shared desk all day. The contract enforces the same boundary from the other side — the
 * queue payload has no `email` or `message` field at all — so this is two independent guards on one rule.
 *
 * ## Why the filters are not in the URL
 *
 * This is the one place the usual advice is wrong. Filter state normally belongs in the query string so a view
 * is linkable and survives a reload; here the search term is free text a staff member types to find a person,
 * which means it can contain a name, an email fragment, or a phone number. A URL is the least private thing in
 * a browser: it goes into history, into the address bar in front of whoever is standing there, into a referrer
 * on any outbound click, and into every server and proxy log along the way.
 *
 * So filters live in component state and nowhere else. Nothing is written to `localStorage` or
 * `sessionStorage`, and nothing is logged. The cost is that a filtered queue is not linkable, which is the
 * right trade for a screen whose search box takes customer PII.
 *
 * ## The assignee filter without a staff directory
 *
 * The API accepts an assignee filter of `unassigned` or a specific staff id, but there is no endpoint that
 * lists staff — `staff-directory.ts` only resolves one id to validate a write. Rather than invent a numeric-id
 * input nobody can use, the filter offers the three answers that need no directory: anyone, nobody, or me. The
 * session supplies the id for "me". A named-person filter waits for a directory endpoint, which is a server
 * change and not this task's.
 */
import type {
  SupportRequestCategory,
  SupportRequestList,
  SupportRequestListQuery,
  SupportRequestQueueItem,
  SupportRequestStatus,
} from '@apuestapr/contracts';
import {
  supportRequestCategories,
  supportRequestStatuses,
  supportContractLimits,
} from '@apuestapr/contracts';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  icons,
  Input,
  Select,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@apuestapr/ui';
import { useId, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { formatPuertoRicoInstant, puertoRicoZoneLabel } from '../format/instants.ts';
import { supportRequestPath } from './paths.ts';
import { supportCategoryLabels, supportStatusBadge, supportStatusLabels } from './vocabulary.ts';

/** "Anyone" is the absence of the filter; the other two are the values the contract accepts. */
export type AssigneeChoice = 'any' | 'unassigned' | 'mine';

export interface SupportQueueFilters {
  readonly status: SupportRequestStatus | 'any';
  readonly category: SupportRequestCategory | 'any';
  readonly assignee: AssigneeChoice;
  readonly search: string;
}

export const emptySupportQueueFilters: SupportQueueFilters = {
  status: 'any',
  category: 'any',
  assignee: 'any',
  search: '',
};

export const defaultSupportPageSize = 25;

/**
 * Filters plus a page, as the query the adapter sends.
 *
 * `any` and an empty search become absent rather than being sent as sentinels, so the server sees a request
 * with no such filter instead of one filtering on a value it would have to interpret.
 */
export const supportQueryFor = (
  filters: SupportQueueFilters,
  page: number,
  selfId: number,
): SupportRequestListQuery => {
  const search = filters.search.trim();
  return {
    page,
    pageSize: defaultSupportPageSize,
    ...(filters.status === 'any' ? {} : { status: filters.status }),
    ...(filters.category === 'any' ? {} : { category: filters.category }),
    ...(filters.assignee === 'any'
      ? {}
      : { assignee: filters.assignee === 'unassigned' ? 'unassigned' : selfId }),
    ...(search === '' ? {} : { search }),
  };
};

const assigneeLabels: Readonly<Record<AssigneeChoice, string>> = {
  any: 'Cualquiera',
  unassigned: 'Sin asignar',
  mine: 'Asignadas a mí',
};

export interface SupportQueueFiltersFormProps {
  readonly filters: SupportQueueFilters;
  readonly onApply: (filters: SupportQueueFilters) => void;
  readonly disabled: boolean;
}

/**
 * The filters, applied on submit rather than on every keystroke.
 *
 * A search box that requested on each character would send a partial name to the server several times per
 * word — more requests carrying PII than the one the staff member intended, and a moving target for whoever is
 * reading the table.
 */
export const SupportQueueFiltersForm = ({
  filters,
  onApply,
  disabled,
}: SupportQueueFiltersFormProps): ReactElement => {
  const [draft, setDraft] = useState<SupportQueueFilters>(filters);
  const headingId = useId();

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onApply(draft);
  };

  return (
    <form
      aria-labelledby={headingId}
      className="mb-4 grid gap-4 rounded-xl border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4"
      noValidate
      onSubmit={submit}
    >
      <h3 className="m-0 sm:col-span-2 lg:col-span-4" id={headingId}>
        Filtros
      </h3>

      <Field label="Estado">
        {(control) => (
          <Select
            {...control}
            disabled={disabled}
            value={draft.status}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                status: event.target.value as SupportQueueFilters['status'],
              }))
            }
          >
            <option value="any">Todos</option>
            {supportRequestStatuses.map((status) => (
              <option key={status} value={status}>
                {supportStatusLabels[status]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Categoría">
        {(control) => (
          <Select
            {...control}
            disabled={disabled}
            value={draft.category}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                category: event.target.value as SupportQueueFilters['category'],
              }))
            }
          >
            <option value="any">Todas</option>
            {supportRequestCategories.map((category) => (
              <option key={category} value={category}>
                {supportCategoryLabels[category]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field label="Asignación">
        {(control) => (
          <Select
            {...control}
            disabled={disabled}
            value={draft.assignee}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                assignee: event.target.value as AssigneeChoice,
              }))
            }
          >
            {(['any', 'unassigned', 'mine'] as const).map((choice) => (
              <option key={choice} value={choice}>
                {assigneeLabels[choice]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Field
        label="Buscar"
        description="Se envía al servidor al aplicar. No se guarda en el navegador ni en la dirección."
      >
        {(control) => (
          <Input
            {...control}
            type="search"
            autoComplete="off"
            maxLength={supportContractLimits.search}
            disabled={disabled}
            value={draft.search}
            onChange={(event) =>
              setDraft((current) => ({ ...current, search: event.target.value }))
            }
          />
        )}
      </Field>

      <div className="flex flex-wrap gap-2 sm:col-span-2 lg:col-span-4">
        <Button type="submit" disabled={disabled}>
          Aplicar filtros
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => {
            setDraft(emptySupportQueueFilters);
            onApply(emptySupportQueueFilters);
          }}
        >
          Limpiar filtros
        </Button>
      </div>
    </form>
  );
};

const QueueInstant = ({ instant }: { readonly instant: string }): ReactElement => (
  <span className="grid gap-1">
    <time dateTime={instant}>{formatPuertoRicoInstant(instant)}</time>
    <span className="text-xs text-muted-foreground">{puertoRicoZoneLabel}</span>
  </span>
);

const StatusBadge = ({ status }: { readonly status: SupportRequestStatus }): ReactElement => {
  const { variant, shape } = supportStatusBadge[status];
  return (
    <Badge variant={variant} shape={shape}>
      {supportStatusLabels[status]}
    </Badge>
  );
};

export interface SupportQueueTableProps {
  readonly list: SupportRequestList;
}

export const SupportQueueTable = ({ list }: SupportQueueTableProps): ReactElement => (
  <div
    aria-label="Tabla de solicitudes de soporte"
    className="overflow-x-auto rounded-xl border border-border bg-card text-card-foreground"
    role="region"
    // Horizontal overflow is a named keyboard-reachable region, not a pointer-only viewport.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    tabIndex={0}
  >
    <Table>
      <TableCaption>
        {String(list.total)} {list.total === 1 ? 'solicitud' : 'solicitudes'} en total, página{' '}
        {String(list.page)} de {String(Math.max(1, list.totalPages))}, en el orden provisto por el
        servidor.
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>Identificador</TableHead>
          <TableHead>Cliente</TableHead>
          <TableHead>Categoría</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead>Asignada a</TableHead>
          <TableHead>Creada</TableHead>
          <TableHead>Actualizada</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {/* API order, rendered as received. Nothing here sorts. */}
        {list.items.map((item: SupportRequestQueueItem) => (
          <TableRow key={item.publicId} data-support-id={item.publicId}>
            <TableHead className="min-w-64 text-foreground" scope="row">
              <Link className="underline" to={supportRequestPath(item.publicId)}>
                {item.publicId}
              </Link>
            </TableHead>
            <TableCell>{item.customerName ?? 'Sin nombre'}</TableCell>
            <TableCell>{supportCategoryLabels[item.category]}</TableCell>
            <TableCell>
              <StatusBadge status={item.status} />
            </TableCell>
            <TableCell>{item.assignedTo?.name ?? 'Sin asignar'}</TableCell>
            <TableCell className="min-w-52">
              <QueueInstant instant={item.createdAt} />
            </TableCell>
            <TableCell className="min-w-52">
              <QueueInstant instant={item.updatedAt} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export interface SupportQueuePaginationProps {
  readonly list: SupportRequestList;
  readonly disabled: boolean;
  readonly onPage: (page: number) => void;
}

export const SupportQueuePagination = ({
  list,
  disabled,
  onPage,
}: SupportQueuePaginationProps): ReactElement => {
  const totalPages = Math.max(1, list.totalPages);
  return (
    <nav aria-label="Paginación de solicitudes" className="mt-4 flex items-center gap-3">
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || list.page <= 1}
        onClick={() => onPage(list.page - 1)}
      >
        Anterior
      </Button>
      <p aria-live="polite" className="m-0">
        Página {String(list.page)} de {String(totalPages)}
      </p>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || list.page >= totalPages}
        onClick={() => onPage(list.page + 1)}
      >
        Siguiente
      </Button>
    </nav>
  );
};

/** The empty state, which is a valid answer and not a fault — an empty queue means nobody is waiting. */
export const SupportQueueEmpty = ({
  filtered,
  onClear,
}: {
  readonly filtered: boolean;
  readonly onClear: () => void;
}): ReactElement => (
  <EmptyState
    icon={icons.inbox}
    title={
      filtered ? 'Ninguna solicitud coincide con los filtros' : 'No hay solicitudes de soporte'
    }
    description={
      filtered ? 'Ajusta o quita un filtro para ver más resultados.' : 'Todo al día por ahora.'
    }
    // A button, not a link: clearing filters changes state on this screen and navigates nowhere.
    action={
      filtered ? (
        <Button size="sm" variant="outline" onClick={onClear}>
          Limpiar filtros
        </Button>
      ) : undefined
    }
  />
);
